import {
  ActivatedRouteSnapshot,
  ResolveFn,
  RouterStateSnapshot,
  Routes,
} from '@angular/router';
import { environment } from '@environments/environment';
import { Dashboard } from '@app/dashboard/dashboard';
import { InvoicesTable } from '@app/invoices-table/invoices-table';
import { CompanyTable } from '@app/company-table/company-table';
import { CompanyDetail } from './company-detail/company-detail';
import { inject } from '@angular/core';
import { LayoutService, PageLayout } from './layout/layout.service';
import { setLayout } from './layout/layout.resolver';
import { HomeRedirectComponent } from './home-redirect/home-redirect.component';
import { NewInvoiceComponent } from './new-invoice/new-invoice.component';
import { NewCompanyComponent } from './new-company/new-company.component';
import { DichiarazioneRedditiComponent } from './dichiarazione-redditi/dichiarazione-redditi.component';
import { FiscalPlanComponent } from './fiscal-plan/fiscal-plan.component';
import { ProfileComponent } from './profile/profile.component';
import { InvoiceDetailComponent } from './invoice-detail/invoice-detail.component';
import { SavedProductsComponent } from './savedproducts/savedproducts.component';
import { NewSavedproductComponent } from './new-savedproduct/new-savedproduct.component';
import { SavedproductDetailsComponent } from './savedproduct-details/savedproduct-details.component';
import { DraftsTable } from './drafts-table/drafts-table';
import { AuthGuard } from './_guards/auth.guard';
import { consentGuard } from './_guards/consent.guard';
import { DocumentsTableComponent } from './documents-table/documents-table.component';
import { MessagesComponent } from './messages/messages.component';
import { InvitationComponent } from './invitation/invitation.component';
import { NewOrganizationComponent } from './new-organization/new-organization.component';
import { UploadXmlComponent } from './upload-xml/upload-xml.component';
import { AcceptInvitationComponent } from './accept-invitation/accept-invitation.component';
import { PreventivesTableComponent } from './preventives-table/preventives-table.component';
import { NewPreventiveComponent } from './new-preventive/new-preventive.component';
import { PreventiveDetailsComponent } from './preventive-details/preventive-details.component';
import { UploadCompanyListComponent } from './upload-company-list/upload-company-list.component';
import { CanDeactivateGuard } from './_guards/can-deactivate.guard';
import { GuidesAndFaqSectionComponent } from './guides-and-faq-section/guides-and-faq-section.component';
import { ReferralRequestTableComponent } from './referral-request-table/referral-request-table.component';
import { CommercialistaTicketingComponent } from './commercialista-ticketing/commercialista-ticketing.component';
import { CorrispettiviReceiptsComponent } from './corrispettivi-receipts/corrispettivi-receipts.component';
import { CorrispettiviReceiptsDetailComponent } from './corrispettivi-receipts-details/corrispettivi-receipts-details.component';
import { UploadCorrispettiviXmlComponent } from './upload-corrispettivi-xml/upload-corrispettivi-xml.component';
import { PlannerBoardComponent } from './planner-board/planner-board.component';

export const organizationIdResolver: ResolveFn<string | number | undefined> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const layoutService = inject(LayoutService);
  let organizationId = route.paramMap.get('organizationId')!;
  // .getHero(
  // );
  layoutService.setOrganizationId(organizationId);
  return organizationId;
};

function baseResolve(pl: PageLayout) {
  return {
    layout: setLayout(pl),
    organizationId: organizationIdResolver,
  };
}

export const routes: Routes = [
  // {
  //   path: ':organizationId/',
  //   resolve: baseResolve(PageLayout.Authorized),

  //   children: [
  //   ]
  // },

  // TODO: solve url error for fiscal plan
  {
    path: ':organizationId/dashboard',
    resolve: {
      layout: setLayout(PageLayout.Authorized),
      organizationId: organizationIdResolver,
    },
    // resolve: baseResolve(PageLayout.Authorized),
    component: Dashboard,
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/invoices_table',
    component: InvoicesTable,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/company_table',
    component: CompanyTable,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/company/:id',
    component: CompanyDetail,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/new_invoice',
    component: NewInvoiceComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
    canDeactivate: [CanDeactivateGuard],
  },
  {
    path: ':organizationId/new_company',
    component: NewCompanyComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/dichiarazione',
    component: DichiarazioneRedditiComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/fiscal_plan',
    component: FiscalPlanComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/bacheca',
    component: PlannerBoardComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/profile',
    component: ProfileComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/invoice/:id',
    component: InvoiceDetailComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/savedproducts_table',
    component: SavedProductsComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/new_savedproduct',
    component: NewSavedproductComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/savedproduct/:id',
    component: SavedproductDetailsComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/preventives_table',
    component: PreventivesTableComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/new_preventive',
    component: NewPreventiveComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/preventive/:id',
    component: PreventiveDetailsComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/drafts_table',
    component: DraftsTable,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/inbox',
    component: MessagesComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/documents_table',
    component: DocumentsTableComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/guides',
    component: GuidesAndFaqSectionComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/referral',
    component: ReferralRequestTableComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/upload_xml',
    component: UploadXmlComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/upload_company_list_from_excel',
    component: UploadCompanyListComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: 'accept-invitation/:uuid',
    component: AcceptInvitationComponent,
    resolve: baseResolve(PageLayout.UnAuthorized),
    // canActivate: [AuthGuard],
  },
  // Zona pubblica di onboarding: nessun login, nessuna Organization. Deve stare
  // prima del catch-all ':organizationId**', che altrimenti se la mangerebbe.
  // Lazy: shell, step e mock non entrano nel bundle iniziale.
  {
    path: 'onboarding',
    // Non per uiltoscana: i link legali (privacy/cookie/termini) non sono ancora
    // pubblicati e il contratto backend esiste solo per BEIZY (beizy_v1).
    canMatch: [() => environment.style !== 'uiltoscana'],
    resolve: baseResolve(PageLayout.UnAuthorized),
    loadChildren: () =>
      import('./onboarding/onboarding.routes').then((m) => m.ONBOARDING_ROUTES),
  },
  {
    path: ':organizationId/commercialista-tickets',
    component: CommercialistaTicketingComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/corrispettivi-receipts',
    component: CorrispettiviReceiptsComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/corrispettivi-receipts-detail/:id',
    component: CorrispettiviReceiptsDetailComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  {
    path: ':organizationId/upload_corrispettivi_xml',
    component: UploadCorrispettiviXmlComponent,
    resolve: baseResolve(PageLayout.Authorized),
    canActivate: [AuthGuard, consentGuard],
  },
  { path: 'invitation/send', component: InvitationComponent },
  // {
  //   path: 'new-organization',
  //   component: NewOrganizationComponent,
  //   // resolve: baseResolve(PageLayout.Authorized),
  // },
  {
    path: ':organizationId**',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'home-redirect',
    component: HomeRedirectComponent,
    // resolve: baseResolve(PageLayout.Authorized),
  },
  {
    path: '**',
    redirectTo: 'home-redirect',
    pathMatch: 'full',
  },

  // id company, router link (ng)
];
